import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { newsletterApi } from '@/lib/api';
import { Mail, Download, Trash2, Search, Send, Image, X, Clock, Users, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const AdminNewsletter = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('compose');
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);

  const [compose, setCompose] = useState({
    subject: '',
    content: '',
    image: null,
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [subs, hist] = await Promise.all([
        newsletterApi.getSubscribers(),
        newsletterApi.getHistory().catch(() => [])
      ]);
      setSubscribers(subs);
      setHistory(hist);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnsubscribe = async (email) => {
    if (!window.confirm(`Remove ${email} from subscribers?`)) return;
    try {
      await newsletterApi.unsubscribe(email);
      toast.success('Subscriber removed');
      loadData();
    } catch (error) {
      toast.error('Failed');
    }
  };

  const handleExport = () => {
    window.open(newsletterApi.exportCsv(), '_blank');
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be under 2MB');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setCompose({ ...compose, image: reader.result });
    };
    reader.readAsDataURL(file);
  };

  const handleSend = async () => {
    if (!compose.subject.trim() || !compose.content.trim()) {
      toast.error('Subject and content are required');
      return;
    }
    if (subscribers.length === 0) {
      toast.error('No subscribers to send to');
      return;
    }
    if (!window.confirm(`Send this newsletter to ${subscribers.length} subscriber${subscribers.length > 1 ? 's' : ''}?`)) return;

    setSending(true);
    try {
      const result = await newsletterApi.send(compose);
      toast.success(result.message);
      setCompose({ subject: '', content: '', image: null });
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to send newsletter');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const filteredSubscribers = subscribers.filter(s =>
    s.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const tabs = [
    { id: 'compose', label: 'Compose', icon: Send },
    { id: 'subscribers', label: `Subscribers (${subscribers.length})`, icon: Users },
    { id: 'history', label: 'History', icon: Clock },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-800">Newsletter</h1>
          <p className="text-stone-600">{subscribers.length} subscribers</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-stone-100 p-1 rounded-lg w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-600 hover:text-stone-900'
            }`}
            data-testid={`newsletter-tab-${tab.id}`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Compose Tab */}
      {activeTab === 'compose' && (
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div>
                <Label>Subject *</Label>
                <Input
                  value={compose.subject}
                  onChange={(e) => setCompose({ ...compose, subject: e.target.value })}
                  placeholder="Newsletter subject line"
                  data-testid="newsletter-subject-input"
                />
              </div>

              <div>
                <Label>Content *</Label>
                <Textarea
                  value={compose.content}
                  onChange={(e) => setCompose({ ...compose, content: e.target.value })}
                  placeholder="Write your newsletter content here...&#10;&#10;Line breaks will be preserved in the email."
                  rows={12}
                  className="font-normal"
                  data-testid="newsletter-content-input"
                />
                <p className="text-xs text-stone-400 mt-1">Line breaks will be preserved. Use blank lines for paragraphs.</p>
              </div>

              {/* Image Upload */}
              <div>
                <Label>Image (optional)</Label>
                {compose.image ? (
                  <div className="relative mt-2 inline-block">
                    <img src={compose.image} alt="Newsletter" className="max-h-48 rounded-lg border" />
                    <button
                      onClick={() => setCompose({ ...compose, image: null })}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      className="mt-1"
                      data-testid="newsletter-image-upload-btn"
                    >
                      <Image className="h-4 w-4 mr-2" />
                      Add Image
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </div>
                )}
              </div>

              {/* Send */}
              <div className="flex items-center justify-between pt-4 border-t">
                <p className="text-sm text-stone-500">
                  Will be sent to <strong>{subscribers.length}</strong> subscriber{subscribers.length !== 1 ? 's' : ''}
                </p>
                <Button
                  onClick={handleSend}
                  disabled={sending || !compose.subject.trim() || !compose.content.trim()}
                  className="bg-amber-700 hover:bg-amber-800"
                  data-testid="newsletter-send-btn"
                >
                  <Send className="h-4 w-4 mr-2" />
                  {sending ? 'Sending...' : 'Send Newsletter'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Subscribers Tab */}
      {activeTab === 'subscribers' && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="relative max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                <Input
                  placeholder="Search subscribers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button onClick={handleExport} variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" /> Export CSV
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-700 mx-auto"></div>
              </div>
            ) : filteredSubscribers.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Subscribed</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSubscribers.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.email}</TableCell>
                      <TableCell>{item.name || '-'}</TableCell>
                      <TableCell>{formatDate(item.subscribed_at)}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleUnsubscribe(item.email)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8">
                <Mail className="h-12 w-12 text-stone-400 mx-auto mb-4" />
                <p className="text-stone-600">{searchTerm ? 'No subscribers found' : 'No subscribers yet'}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <Card>
          <CardContent className="p-4">
            {history.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Subject</TableHead>
                    <TableHead>Recipients</TableHead>
                    <TableHead>Failed</TableHead>
                    <TableHead>Sent</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.subject}</TableCell>
                      <TableCell>
                        <span className="flex items-center gap-1 text-green-600">
                          <CheckCircle className="h-3.5 w-3.5" /> {item.recipients}
                        </span>
                      </TableCell>
                      <TableCell>
                        {item.failed > 0 ? (
                          <span className="text-red-600">{item.failed}</span>
                        ) : (
                          <span className="text-stone-400">0</span>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(item.sent_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8">
                <Clock className="h-12 w-12 text-stone-400 mx-auto mb-4" />
                <p className="text-stone-600">No newsletters sent yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminNewsletter;
